import { useState } from "react";
import { Building2, ChevronDown } from "lucide-react";
import { useOrganization } from "@/context/Organization";
import { cn } from "@/lib/utils";

/**
 * Sélecteur d'organisation (plan V4 §40).
 *
 * Même habillage que `SectorSwitcher` — un `<select>` natif dans un bouton
 * d'en-tête : le menu reste celui du système, donc utilisable au clavier et sur
 * mobile sans réécrire de composant.
 *
 * Ne s'affiche PAS quand le membre n'appartient qu'à une organisation : dans le
 * cas très majoritaire (un client, une organisation), le CRM ne doit pas se
 * mettre à parler d'un concept qui ne sert à rien. C'est aussi ce qui garantit
 * qu'aucun écran existant ne change d'aspect après la migration.
 *
 * Changer d'organisation n'est pas un choix côté client : le composant appelle
 * `POST /organizations/{id}/switch`, le backend vérifie l'appartenance et rend
 * un nouveau jeton (§10). Un utilisateur qui bricolerait la valeur du select
 * obtiendrait un 403, pas les données du voisin.
 */
export default function OrganizationSwitcher({ dark = false, className }) {
  const { current, organizations, switchTo } = useOrganization();
  const [busy, setBusy] = useState(false);

  if (!current || (organizations || []).length < 2) return null;

  const onChange = async (id) => {
    if (!id || id === current.id) return;
    setBusy(true);
    try {
      await switchTo(id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <label
      className={cn(
        "relative flex h-[42px] cursor-pointer items-center gap-[7px] rounded-[11px] border px-3 text-[13px] font-medium transition-colors",
        "focus-within:outline focus-within:outline-2 focus-within:outline-offset-2",
        dark
          ? "border-[#22304a] bg-white/[.06] text-[#e2e8f0] hover:border-[#3d5170] focus-within:outline-[#4f9dff]"
          : "border-[#e3e3e3] bg-white text-[#303030] hover:bg-[#fafafa] focus-within:outline-[#005bd3]",
        busy && "pointer-events-none opacity-60",
        className
      )}
      title="Organisation active"
    >
      <Building2 size={15} className={cn("shrink-0", dark ? "text-[#7fb4ff]" : "text-[#005bd3]")} />
      <select
        data-testid="organization-switcher"
        value={current.id}
        disabled={busy}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Organisation active"
        className={cn(
          "min-w-0 flex-1 cursor-pointer appearance-none bg-transparent pr-5 text-[13px] font-semibold outline-none",
          dark
            ? "text-[#e2e8f0] [&>option]:bg-[#0f172a] [&>option]:text-[#e2e8f0]"
            : "text-[#00427a]"
        )}
      >
        {organizations.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <ChevronDown size={14} className={cn("pointer-events-none absolute right-3 shrink-0", dark ? "text-[#64748b]" : "text-[#94a3b8]")} />
    </label>
  );
}

/**
 * Nom de l'organisation active, en texte seul.
 *
 * Utilisé là où l'écran doit RAPPELER dans quelle organisation on travaille
 * sans offrir d'en changer (§40 : « afficher l'organisation active dans les
 * écrans importants »). Muet tant qu'il n'y en a qu'une.
 */
export function OrganizationBadge({ className }) {
  const { current, organizations } = useOrganization();
  if (!current || (organizations || []).length < 2) return null;
  return (
    <span
      data-testid="organization-badge"
      className={cn("inline-flex items-center gap-1.5 rounded-full bg-[#eaf4ff] px-2.5 py-1 text-[12px] font-medium text-[#00427a]", className)}
    >
      <Building2 size={13} className="shrink-0" />
      {current.name}
    </span>
  );
}
